import { devLogger } from "@/utils/devLogger";

export const AUTH_CHANNEL = "okta/auth";
export const LOGGED_OUT = "okta/logged_out";
export const TOKEN_REFRESHED = "okta/token_refreshed";
export const TOKEN_REFRESH_LOCK = "okta/token_refresh_lock";
export const USER_ACTIVITY_DETECTED = "okta/user_activity_detected";
// Type definitions for all broadcast events

type AccessToken = string;
type IDToken = string;

export type LoggedOutEvent = {
  type: typeof LOGGED_OUT;
};

export type TokenRefreshedEvent = {
  type: typeof TOKEN_REFRESHED;
  accessToken: AccessToken;
  idToken: IDToken;
};

export type TokenRefreshLockEvent = {
  type: typeof TOKEN_REFRESH_LOCK;
  lock: boolean;
  owner: string;
};

export type UserActivityDetectedEvent = {
  type: typeof USER_ACTIVITY_DETECTED;
};

// Union type for all possible events
export type AuthBroadcastEvent =
  | LoggedOutEvent
  | TokenRefreshedEvent
  | TokenRefreshLockEvent
  | UserActivityDetectedEvent;

// Type checking helpers
export function isLoggedOutEvent(
  event: MessageEvent
): event is MessageEvent<LoggedOutEvent> {
  return event.data.type === LOGGED_OUT;
}

export function isTokenRefreshedEvent(
  event: MessageEvent
): event is MessageEvent<TokenRefreshedEvent> {
  return event.data.type === TOKEN_REFRESHED;
}

export function isTokenRefreshLockEvent(
  event: MessageEvent
): event is MessageEvent<TokenRefreshLockEvent> {
  return event.data.type === TOKEN_REFRESH_LOCK;
}

export function isUserActivityDetectedEvent(
  event: MessageEvent
): event is MessageEvent<UserActivityDetectedEvent> {
  return event.data.type === USER_ACTIVITY_DETECTED;
}

// Broadcast functions with type safety
export function broadcastLogout() {
  devLogger.log("broadcastLogout");
  const channel = new BroadcastChannel(AUTH_CHANNEL);
  channel.postMessage({ type: LOGGED_OUT } satisfies LoggedOutEvent);
  channel.close();
}

export function broadcastTokenRefreshed({
  accessToken,
  idToken
}: {
  accessToken: AccessToken;
  idToken: IDToken;
}) {
  devLogger.log("broadcastTokenRefreshed");
  const channel = new BroadcastChannel(AUTH_CHANNEL);
  channel.postMessage({
    type: TOKEN_REFRESHED,
    accessToken,
    idToken
  } satisfies TokenRefreshedEvent);
  channel.close();
}

export function broadcastTokenRefreshLock(lock: boolean, owner: string) {
  devLogger.log("broadcastTokenRefreshLock", lock, owner);
  const channel = new BroadcastChannel(AUTH_CHANNEL);
  channel.postMessage({
    type: TOKEN_REFRESH_LOCK,
    lock,
    owner
  } satisfies TokenRefreshLockEvent);
  channel.close();
}

export function broadcastUserActivityDetected() {
  devLogger.log("broadcastUserActivityDetected");
  const channel = new BroadcastChannel(AUTH_CHANNEL);
  channel.postMessage({
    type: USER_ACTIVITY_DETECTED
  } satisfies UserActivityDetectedEvent);
  channel.close();
}
